// SPDX-License-Identifier: AGPL-3.0-or-later

import {clearAllAttachmentMocks, setAttachmentMock} from '@app/features/devtools/commands/DeveloperOptionsCommands';
import DeveloperMode from '@app/features/devtools/state/DeveloperMode';
import DeveloperOptions from '@app/features/devtools/state/DeveloperOptions';
import type {Message} from '@app/features/messaging/models/Message';
import {
	type MediaMenuDataProps,
	mediaMenuItemIds,
	useMediaMenuData,
} from '@app/features/ui/action_menu/items/MediaMenuData';
import {ReverseImageSearchMenuItems} from '@app/features/ui/action_menu/items/ReverseImageSearchMenuItems';
import {MenuGroup} from '@app/features/ui/action_menu/MenuGroup';
import {MenuItem} from '@app/features/ui/action_menu/MenuItem';
import {MenuItemSubmenu} from '@app/features/ui/action_menu/MenuItemSubmenu';
import {MessageContextMenu} from '@app/features/ui/action_menu/MessageContextMenu';
import type {MenuGroupType, MenuItemType} from '@app/features/ui/menu_bottom_sheet/MenuBottomSheet';
import {MS_PER_DAY, MS_PER_HOUR} from '@app/vendor/date_utils/DateConstants';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useMemo} from 'react';

type MediaType = 'image' | 'gif' | 'gifv' | 'video' | 'audio' | 'file';
interface MediaContextMenuProps {
	message: Message;
	originalSrc: string;
	proxyURL?: string;
	type: MediaType;
	contentHash?: string | null;
	attachmentId?: string;
	embedIndex?: number;
	defaultName?: string;
	defaultAltText?: string;
	snapshotIndex?: number;
	onClose: () => void;
	onDelete: (bypassConfirm?: boolean) => void;
}
const INLINE_MEDIA_ITEM_IDS = new Set<string>([
	mediaMenuItemIds.copy,
	mediaMenuItemIds.save,
	mediaMenuItemIds.copyLink,
	mediaMenuItemIds.openLink,
]);
export const MediaContextMenu: React.FC<MediaContextMenuProps> = observer(
	({
		message,
		originalSrc,
		proxyURL,
		type,
		contentHash,
		attachmentId,
		embedIndex,
		defaultName,
		defaultAltText,
		snapshotIndex,
		onClose,
		onDelete,
	}) => {
		const {i18n} = useLingui();
		const mediaProps: MediaMenuDataProps = useMemo(
			() => ({
				message,
				originalSrc,
				proxyURL,
				type,
				contentHash,
				attachmentId,
				embedIndex,
				defaultName,
				defaultAltText,
				snapshotIndex,
			}),
			[
				message,
				originalSrc,
				proxyURL,
				type,
				contentHash,
				attachmentId,
				embedIndex,
				defaultName,
				defaultAltText,
				snapshotIndex,
			],
		);
		const {groups: mediaGroupsRaw, handlers, state: mediaState} = useMediaMenuData(mediaProps, {onClose});
		const {submenuGroups, inlineCopyItem, inlineSaveItem} = useMemo<{
			submenuGroups: Array<MenuGroupType>;
			inlineCopyItem: MenuItemType | null;
			inlineSaveItem: MenuItemType | null;
		}>(() => {
			let copy: MenuItemType | null = null;
			let save: MenuItemType | null = null;
			const submenu: Array<MenuGroupType> = mediaGroupsRaw
				.map((group) => ({
					items: group.items.filter((item) => {
						const id = 'id' in item ? item.id : undefined;
						if (id === mediaMenuItemIds.copy) {
							copy = item as MenuItemType;
							return false;
						}
						if (id === mediaMenuItemIds.save) {
							save = item as MenuItemType;
							return false;
						}
						if (id && INLINE_MEDIA_ITEM_IDS.has(id)) {
							return false;
						}
						return true;
					}),
				}))
				.filter((group) => group.items.length > 0);
			return {submenuGroups: submenu, inlineCopyItem: copy, inlineSaveItem: save};
		}, [mediaGroupsRaw]);
		const isDev = DeveloperMode.isDeveloper;
		const currentMock = attachmentId ? DeveloperOptions.mockAttachmentStates[attachmentId] : undefined;
		const mockExpiresSoon = () =>
			setAttachmentMock(attachmentId!, {
				expired: false,
				expiresAt: new Date(Date.now() + MS_PER_DAY).toISOString(),
			});
		const mockExpiresWeek = () =>
			setAttachmentMock(attachmentId!, {
				expired: false,
				expiresAt: new Date(Date.now() + 7 * MS_PER_DAY).toISOString(),
			});
		const mockExpired = () =>
			setAttachmentMock(attachmentId!, {
				expired: true,
				expiresAt: new Date(Date.now() - MS_PER_HOUR).toISOString(),
			});
		const clearMock = () => setAttachmentMock(attachmentId!, null);
		const supportsReverseImageSearch = type === 'image' || type === 'gif' || type === 'gifv';
		const inlineMediaItems = (
			<>
				{inlineCopyItem && (
					<MenuItem onClick={inlineCopyItem.onClick}>{inlineCopyItem.label ?? mediaState.copyLabel}</MenuItem>
				)}
				{inlineSaveItem && (
					<MenuItem onClick={inlineSaveItem.onClick}>{inlineSaveItem.label ?? mediaState.saveLabel}</MenuItem>
				)}
			</>
		);
		const attachmentExtraContent = supportsReverseImageSearch ? (
			<ReverseImageSearchMenuItems imageUrl={originalSrc} onClose={onClose} wrapInGroup />
		) : null;
		return (
			<>
				<MessageContextMenu
					message={message}
					onClose={onClose}
					onDelete={onDelete}
					excludeMediaActions
					mediaHandlers={handlers}
					mediaGroups={submenuGroups}
					attachmentId={attachmentId}
					attachmentExtraContent={attachmentExtraContent}
					inlineMediaItems={inlineMediaItems}
				/>
				{isDev && attachmentId && (
					<MenuGroup>
						<MenuItemSubmenu
							label={i18n._(msg`Attachment Mock`)}
							render={() => (
								<>
									<MenuItem onClick={mockExpiresSoon}>{i18n._(msg`Mock expires in 1 day`)}</MenuItem>
									<MenuItem onClick={mockExpiresWeek}>{i18n._(msg`Mock expires in 7 days`)}</MenuItem>
									<MenuItem onClick={mockExpired}>{i18n._(msg`Mock expired`)}</MenuItem>
									{currentMock && (
										<MenuItem onClick={clearMock}>{i18n._(msg`Clear mock for this attachment`)}</MenuItem>
									)}
									<MenuItem onClick={clearAllAttachmentMocks}>{i18n._(msg`Clear all attachment mocks`)}</MenuItem>
								</>
							)}
						/>
					</MenuGroup>
				)}
			</>
		);
	},
);
MediaContextMenu.displayName = 'MediaContextMenu';
